/**
 * 黑名单围栏审核页（总部地图工具）
 *
 * 内容复制自「围栏黑名单」功能的围栏 Tab（GeofenceListTab），区别：
 * - 全量围栏（不按仓库范围，不传 warehouseId）
 * - 复用详情页表格列（buildGeofenceColumns）；支持邮编/状态/名称筛选、分页
 * - 「查看」「审核」按钮均按行 zipcode 在右侧抽屉打开地图详情（在抽屉内进行终审）
 */
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Select, Table, Modal, message, DatePicker } from 'antd';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import InputFilterItem from '../../delivery/components/InputFilterItem';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { getSmartLabelClassName } from '../../../utils/labelWidthUtils';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../utils/languageUtils';
import { buildGeofenceColumns } from '../../wms/zipcode_map_geofence/components/GeofenceColumns';
import { ZipcodeMapGeofenceType } from '../../wms/zipcode_map_geofence/graphql/queries/getZipcodeGeofenceListing';
import {
    GET_ZIPCODE_GEOFENCE_PAGE_LISTING,
    GetZipcodeGeofencePageListingResponse,
    ZipcodeGeofenceFiltersType,
} from '../../wms/zipcode_map_geofence/graphql/queries/getZipcodeGeofencePageListing';
import { DELETE_ZIPCODE_GEOFENCE, DeleteZipcodeGeofenceResponse } from '../../wms/zipcode_map_geofence/graphql/mutations/deleteZipcodeGeofence';
import ZipcodeMapDrawer from '../../wms/zipcode_map_geofence/components/ZipcodeMapDrawer';
import { GET_ALL_ACCOUNTS_FOR_SELECT, GetAllAccountsForSelectResponse } from '../location-correction/graphql/queries/getAllAccountsForSelect';
import { GET_WAREHOUSES_FOR_HEADQUARTERS, WarehousesResponse } from '../../../graphql/queries/getWarehouseForHeadquarters';
import { SiteType } from '../../../graphql/types/SiteType';
import { ResourceCode } from '../../../constants/resourceCodes';

const { RangePicker } = DatePicker;

type GeofenceFilterState = {
    zipcode?: string;
    geofenceStatus?: string;
    geofenceName?: string;
    creatorId?: number;
    createdAtRange?: [Dayjs | null, Dayjs | null] | null;
    hubId?: number;
    warehouseId?: number;
};

const GeofenceBlacklistReviewPage: React.FC = () => {
    const { t } = useTranslation();

    const [tempFilters, setTempFilters] = useState<GeofenceFilterState>({});
    const [filters, setFilters] = useState<GeofenceFilterState>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [drawerZipcode, setDrawerZipcode] = useState<string | null>(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [currentDeleteRecord, setCurrentDeleteRecord] = useState<ZipcodeMapGeofenceType | null>(null);
    // 创建人远程搜索：搜索文本 + 当前选中项（搜索结果变化后仍要能正常显示已选中的人）
    const [creatorSearchText, setCreatorSearchText] = useState('');
    const [selectedCreator, setSelectedCreator] = useState<{ label: string; value: number } | null>(null);

    // 全量围栏：不传 warehouseId（仅当用户选了所属仓时才带上）；创建时间区间按天转 epoch 秒
    const queryFilters: ZipcodeGeofenceFiltersType = {
        zipcode: filters.zipcode,
        geofenceStatus: filters.geofenceStatus,
        geofenceName: filters.geofenceName,
        creatorId: filters.creatorId,
        hubId: filters.hubId,
        warehouseId: filters.warehouseId,
        createdAtFrom: filters.createdAtRange?.[0]?.startOf('day').unix(),
        createdAtTo: filters.createdAtRange?.[1]?.endOf('day').unix(),
    };

    // 中心仓/所属仓下拉：全部站点（总部）
    const { data: warehouseData } = useQuery<WarehousesResponse>(GET_WAREHOUSES_FOR_HEADQUARTERS);

    // 创建人下拉：按姓名/手机号远程搜索，实现与地址定位审核页的审核人筛选一致（getAllAccountsForSelect，服务端固定最多 10 条）。
    // 不再全量拉取账号：原全量接口返回约 5 万账号、13MB，Apollo 归一化 + Select 渲染会阻塞主线程 3 秒以上。
    // 搜索文本为空时同样发请求，带出默认 10 条，保证下拉点开不为空白。
    const [searchCreators, { data: creatorData, loading: creatorLoading }] = useLazyQuery<GetAllAccountsForSelectResponse>(
        GET_ALL_ACCOUNTS_FOR_SELECT,
        {
            fetchPolicy: 'network-only',
        }
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            const keyword = creatorSearchText.trim();
            searchCreators({
                variables: {
                    name: keyword || undefined,
                    phone: keyword || undefined,
                },
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [creatorSearchText, searchCreators]);

    const creatorOptions = useMemo(() => {
        const searched = (creatorData?.getAllAccountsForSelect || []).map((a) => ({
            label: `${a.name}${a.phone ? `(${a.phone})` : ''}`,
            value: Number(a.id),
        }));
        // 已选中项不在本次搜索结果中时补进去，避免 Select 只显示 id
        if (selectedCreator && !searched.some((o) => o.value === selectedCreator.value)) {
            return [selectedCreator, ...searched];
        }
        return searched;
    }, [creatorData, selectedCreator]);
    const hubOptions = useMemo(
        () => (warehouseData?.warehouses || [])
            .filter((w) => w.siteType === SiteType.HUB)
            .map((w) => ({ label: w.name, value: Number(w.id) })),
        [warehouseData]
    );
    // 所属仓选项随中心仓级联：选了 hub 只显示该 hub 下的，否则显示全部所属仓
    const warehouseOptions = useMemo(
        () => (warehouseData?.warehouses || [])
            .filter((w) => w.siteType === SiteType.WAREHOUSE)
            .filter((w) => tempFilters.hubId == null || String(w.parentSiteId) === String(tempFilters.hubId))
            .map((w) => ({ label: w.name, value: Number(w.id) })),
        [warehouseData, tempFilters.hubId]
    );

    const { data, loading, refetch } = useQuery<GetZipcodeGeofencePageListingResponse>(
        GET_ZIPCODE_GEOFENCE_PAGE_LISTING,
        {
            variables: {
                filters: queryFilters,
                pageRequest: {
                    page: currentPage,
                    size: pageSize,
                    sortBy: 'createdAt',
                    sortDirection: 'DESC',
                },
            },
            fetchPolicy: 'cache-and-network',
        }
    );

    const listData = data?.getZipcodeGeofencePageListing?.content || [];
    const totalElements = data?.getZipcodeGeofencePageListing?.totalElements || 0;

    const [deleteGeofence, { loading: deleteLoading }] = useMutation<DeleteZipcodeGeofenceResponse>(
        DELETE_ZIPCODE_GEOFENCE,
        {
            onCompleted: (res) => {
                if (res.deleteZipcodeGeofence) {
                    message.success(t('wms.zipcodeMapGeofence.messages.deleteSuccess'));
                    setDeleteModalVisible(false);
                    setCurrentDeleteRecord(null);
                    refetch();
                } else {
                    message.error(t('wms.zipcodeMapGeofence.messages.deleteFailed'));
                }
            },
            onError: (err) => {
                message.error(t('wms.zipcodeMapGeofence.messages.deleteFailedWithReason', { reason: err.message }));
            },
        }
    );

    // 查看/审核：均按行 zipcode 在右侧抽屉打开地图详情（在抽屉内进行终审）
    const handleNavigateToMap = useCallback((record: ZipcodeMapGeofenceType) => {
        setDrawerZipcode(record.zipcode);
    }, []);

    // 抽屉内可能进行了终审，关闭时刷新列表
    const handleDrawerClose = useCallback(() => {
        setDrawerZipcode(null);
        refetch();
    }, [refetch]);

    const handleDeleteClick = useCallback((record: ZipcodeMapGeofenceType) => {
        setCurrentDeleteRecord(record);
        setDeleteModalVisible(true);
    }, []);

    const columns = buildGeofenceColumns({
        t,
        showZipcodeColumn: true,
        onIdClick: handleNavigateToMap,
        onView: handleNavigateToMap,
        onAudit: handleNavigateToMap,
        onDelete: handleDeleteClick,
        // 终审页：仅「待终审」(APPROVED) 状态可点击审核
        auditableStatus: 'APPROVED',
        auditResourceCodes: [ResourceCode.menu.GEOFENCE_BLACKLIST_REVIEW],
    });

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setTempFilters({});
        setFilters({});
        setCurrentPage(1);
        setCreatorSearchText('');
        setSelectedCreator(null);
    };

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
        }
    };

    const geofenceStatusOptions = [
        { value: 'CREATED', label: t('wms.zipcodeMapGeofence.status.created') },
        { value: 'APPROVED', label: t('wms.zipcodeMapGeofence.status.approved') },
        { value: 'EFFECTIVE', label: t('wms.zipcodeMapGeofence.status.effective') },
        { value: 'INACTIVE', label: t('wms.zipcodeMapGeofence.status.inactive') },
    ];

    const colLabels = [
        t('central.zipcodes.filters.zipcode'),
        t('central.zipcodes.filters.geofenceStatus'),
        t('central.zipcodes.filters.geofenceName'),
        t('wms.zipcodeMapGeofence.filters.creator'),
        t('wms.zipcodeMapGeofence.filters.createdTime'),
        t('wms.zipcodeMapGeofence.filters.hub'),
        t('wms.zipcodeMapGeofence.filters.warehouse'),
    ];
    const colSpanClassName = getSmartLabelClassName(colLabels);
    const statusContainerClassName = useFilterContainerClassName();
    const statusLabelClassName = useFilterLabelClassName(colSpanClassName);
    const statusInputContainerClassName = useFilterInputContainerClassName();

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <div className="mb-2 py-2">
                <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                    {/* 中心仓 */}
                    <div className="min-w-0">
                        <div className={statusContainerClassName}>
                            <span className={statusLabelClassName}>
                                {t('wms.zipcodeMapGeofence.filters.hub')}
                            </span>
                            <div className={statusInputContainerClassName}>
                                <Select
                                    className="w-full"
                                    size="middle"
                                    showSearch
                                    allowClear
                                    optionFilterProp="label"
                                    placeholder={t('components.input.select')}
                                    value={tempFilters.hubId}
                                    options={hubOptions}
                                    onChange={(value?: number) => setTempFilters({
                                        ...tempFilters,
                                        hubId: value ?? undefined,
                                        // 切换中心仓时联动清空所属仓
                                        warehouseId: undefined,
                                    })}
                                />
                            </div>
                        </div>
                    </div>
                    {/* 所属仓（随中心仓级联） */}
                    <div className="min-w-0">
                        <div className={statusContainerClassName}>
                            <span className={statusLabelClassName}>
                                {t('wms.zipcodeMapGeofence.filters.warehouse')}
                            </span>
                            <div className={statusInputContainerClassName}>
                                <Select
                                    className="w-full"
                                    size="middle"
                                    showSearch
                                    allowClear
                                    optionFilterProp="label"
                                    placeholder={t('components.input.select')}
                                    value={tempFilters.warehouseId}
                                    options={warehouseOptions}
                                    onChange={(value?: number) => setTempFilters({
                                        ...tempFilters,
                                        warehouseId: value ?? undefined,
                                    })}
                                />
                            </div>
                        </div>
                    </div>
                    {/* 邮编 */}
                    <div className="min-w-0">
                        <InputFilterItem
                            label={t('central.zipcodes.filters.zipcode')}
                            value={tempFilters.zipcode}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setTempFilters({
                                ...tempFilters,
                                zipcode: e.target.value || undefined,
                            })}
                            spanClassName={colSpanClassName}
                        />
                    </div>
                    {/* 围栏名称 */}
                    <div className="min-w-0">
                        <InputFilterItem
                            label={t('central.zipcodes.filters.geofenceName')}
                            value={tempFilters.geofenceName}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setTempFilters({
                                ...tempFilters,
                                geofenceName: e.target.value || undefined,
                            })}
                            spanClassName={colSpanClassName}
                        />
                    </div>
                    {/* 围栏状态 */}
                    <div className="min-w-0">
                        <div className={statusContainerClassName}>
                            <span className={statusLabelClassName}>
                                {t('central.zipcodes.filters.geofenceStatus')}
                            </span>
                            <div className={statusInputContainerClassName}>
                                <Select
                                    className="w-full"
                                    size="middle"
                                    allowClear
                                    placeholder={t('components.input.select')}
                                    value={tempFilters.geofenceStatus}
                                    options={geofenceStatusOptions}
                                    onChange={(value?: string) => setTempFilters({
                                        ...tempFilters,
                                        geofenceStatus: value || undefined,
                                    })}
                                />
                            </div>
                        </div>
                    </div>
                    {/* 创建人 */}
                    <div className="min-w-0">
                        <div className={statusContainerClassName}>
                            <span className={statusLabelClassName}>
                                {t('wms.zipcodeMapGeofence.filters.creator')}
                            </span>
                            <div className={statusInputContainerClassName}>
                                <Select
                                    className="w-full"
                                    size="middle"
                                    showSearch
                                    allowClear
                                    filterOption={false}
                                    placeholder={t('components.input.select')}
                                    value={tempFilters.creatorId}
                                    options={creatorOptions}
                                    loading={creatorLoading}
                                    notFoundContent={creatorLoading ? t('components.input.select') : null}
                                    onSearch={setCreatorSearchText}
                                    onChange={(value?: number) => {
                                        setSelectedCreator(
                                            value != null
                                                ? (creatorOptions.find((o) => o.value === value) ?? { label: String(value), value })
                                                : null
                                        );
                                        setTempFilters({
                                            ...tempFilters,
                                            creatorId: value ?? undefined,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {/* 创建时间区间 */}
                    <div className="min-w-0">
                        <div className={statusContainerClassName}>
                            <span className={statusLabelClassName}>
                                {t('wms.zipcodeMapGeofence.filters.createdTime')}
                            </span>
                            <div className={statusInputContainerClassName}>
                                <RangePicker
                                    className="w-full"
                                    size="middle"
                                    format="YYYY/MM/DD"
                                    value={tempFilters.createdAtRange ?? null}
                                    onChange={(dates) => setTempFilters({
                                        ...tempFilters,
                                        createdAtRange: dates ? (dates as [Dayjs | null, Dayjs | null]) : null,
                                    })}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mb-2 flex justify-end gap-2">
                <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                    <span className="font-bold">{t('central.zipcodes.filters.filter')}</span>
                </Button>
                <Button onClick={handleResetFilters} className="w-auto min-w-24">
                    <span className="font-bold">{t('central.zipcodes.filters.reset')}</span>
                </Button>
            </div>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={listData}
                loading={loading}
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="middle"
                className="mb-4"
            />

            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
                paginationProps={{ disabled: loading }}
            />

            {/* 删除确认弹窗 */}
            <Modal
                title={t('wms.zipcodeMapGeofence.delete.title')}
                open={deleteModalVisible}
                onCancel={() => { setDeleteModalVisible(false); setCurrentDeleteRecord(null); }}
                footer={[
                    <Button key="cancel" disabled={deleteLoading} onClick={() => { setDeleteModalVisible(false); setCurrentDeleteRecord(null); }}>
                        {t('wms.zipcodeMapGeofence.delete.cancelButton')}
                    </Button>,
                    <Button
                        key="delete"
                        type="primary"
                        danger
                        loading={deleteLoading}
                        onClick={() => currentDeleteRecord?.id && deleteGeofence({ variables: { id: currentDeleteRecord.id } })}
                    >
                        {t('wms.zipcodeMapGeofence.delete.deleteButton')}
                    </Button>,
                ]}
                width={400}
            >
                <div className="py-4">
                    <p className="text-gray-700">{t('wms.zipcodeMapGeofence.delete.confirmMessage')}</p>
                </div>
            </Modal>

            {/* 围栏黑名单详情抽屉（终审） */}
            <ZipcodeMapDrawer
                zipcode={drawerZipcode}
                from="geofence-review"
                onClose={handleDrawerClose}
            />
        </div>
    );
};

export default GeofenceBlacklistReviewPage;
